// Utils and Service
import axios from './instance'
import { getErrorMessage } from '../utils/helpers'

/**
 * Gets the list of attendees.
 * @param {string} url
 * @param {func} setError
 * @param {func} setLoading
 * @returns `data` results
 */
export const getAttendees = async (
  url,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(url)

    setLoading(false)
    setSuccess(data)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Adds the specified attendee from the `payload` to the event with the specified `id`.
 * @param {string} id
 * @param {object} payload attendee details
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` created attendee
 */
export const addAttendee = async (
  id,
  payload,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/events/${id}/attendees/`, payload)

    setLoading(false)
    setSuccess(`${data.firstName} ${data.lastName} created.`)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Updates the attendee with the specified `id`.
 * @param {string} eventId
 * @param {string} payload
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 */
export const updateAttendee = async (
  eventId,
  payload,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.patch(`/events/${eventId}/attendees/${payload.id}/`, payload)

    setLoading(false)
    setSuccess(`${data.firstName} ${data.lastName} updated.`)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Delete the attendee with the specified `id`.
 * @param {string} eventId
 * @param {string} id
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 */
export const deleteAttendee = async (
  eventId,
  id,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    await axios.delete(`/events/${eventId}/attendees/${id}/`)

    setLoading(false)
    setSuccess('Attendee deleted.')
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
  }
}

/**
 * Get the status for the attendee import specified by `taskId`
 * @param {string} eventId
 * @param {string} taskId
 * @param {func} setError
 * @param {func} setLoading
 * @returns response
 */
export const uploadAttendeeList = async (eventId, file, setError, setLoading) => {
  setLoading(true)

  try {
    const response = await axios.post(`/events/${eventId}/attendee_imports/`, file, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Get the status for the attendee import specified by `taskId`
 * @param {string} eventId
 * @param {string} taskId
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns the task status
 */
export const getAttendeeImportStatus = async (eventId, taskId) => {
  try {
    return await axios.get(`/events/${eventId}/attendee_imports/${taskId}/`)
  } catch (err) {
    return null
  }
}

/**
 * Gets the attendee photo for the specified `eventId` and `attendeeId`.
 * @param {string} eventId
 * @param {string} attendeeId
 * @param {func} setError
 * @param {func} setLoading
 */
export const getAttendeePhoto = async (
  eventId,
  attendeeId,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.get(`/events/${eventId}/attendees/${attendeeId}/verification-image/`)

    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the list of attendees that are syncing for an event.
 * @param {string} url
 * @param {func} setError
 * @param {func} setLoading
 * @returns `data` results
 */
export const createAttendeeSync = async (
  eventId,
  payload,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/events/${eventId}/attendee_syncs/`, payload)

    setLoading(false)
    setSuccess(data)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Get the status for the attendee sync specified by `taskId`
 * @param {string} eventId
 * @param {string} taskId
 * @returns the task status
 */
export const getAttendeeSyncStatus = async (eventId, taskId) => {
  try {
    return await axios.get(`/events/${eventId}/attendee_syncs/${taskId}/`)
  } catch (err) {
    return null
  }
}

export const getAttendeeSyncHistory = async (eventId) => {
  try {
    const data = await axios.get(`/events/${eventId}/attendee_syncs/?limit=100`)
    return data
  } catch (err) {
    return null
  }
}

export const markAttendeeSyncFailed = async (eventId, syncId) => {
  try {
    const data = await axios.post(`/events/${eventId}/attendee_syncs/${syncId}/mark-failed/`)
    return data
  } catch (err) {
    return null
  }
}

/**
 * Ping EventsAir with a 1pt query to read the current hourly cost-remaining
 * budget. Returns the response data on success, or `{ error: <message> }`
 * on failure (e.g. non-EventsAir event or transient API error).
 * @param {string} eventId
 */
export const checkEventsAirCost = async (eventId) => {
  try {
    return await axios.post(`/events/${eventId}/attendee_syncs/cost-check/`)
  } catch (err) {
    return { error: getErrorMessage(err) }
  }
}

/**
 * Read whether the next sync click would resume an aborted run.
 * Returns `{ bootstrap, incremental }` where each is either null (no cursor)
 * or `{ offset?, pendingCount?, startedAt }`. Free of EA cost — reads only
 * persisted Event fields.
 * @param {string} eventId
 */
export const getAttendeeSyncResumeStatus = async (eventId) => {
  try {
    return await axios.get(`/events/${eventId}/attendee_syncs/resume-status/`)
  } catch (err) {
    return null
  }
}
